/* ==========================================================================
   Lumora experience layer for Dawn
   - adaptive grid scale-up above 1920px
   - native scrolling (no third-party smooth-scroll script)
   - intro loader -> "ready" gate -> scroll / text reveals
   - liquid cursor-reveal hero, hero card carousel
   - live clock, count-up stats, nav overlay, request modal
   Plain JS, no build step.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var cfg = window.LuConfig || {};
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var LENIS_URLS = []; // no third-party script is loaded: smooth scrolling is native

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function clamp(n, a, b) { return Math.min(b, Math.max(a, n)); }
  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  /* ---------------------------------------------------------------- grid */
  function applyAdaptiveGrid() {
    var FONT_BASE = 16, baseWidth = 1920, coef = 0.6666;
    var w = window.innerWidth;
    var widthReduction = ((baseWidth - w) / baseWidth) * 100; // negative when w > baseWidth
    var size = FONT_BASE - (FONT_BASE * (widthReduction * coef)) / 100;
    if (size > FONT_BASE) root.style.setProperty('--lu', size + 'px');
    else root.style.removeProperty('--lu');
  }
  applyAdaptiveGrid();
  window.addEventListener('resize', applyAdaptiveGrid);

  /* -------------------------------------------------------- scroll + lock */
  var lenis = null;
  var locks = {};
  function lockCount() { return Object.keys(locks).length; }
  function stopScroll(key) {
    locks[key] = true;
    if (lenis) lenis.stop();
    root.classList.add('lu-locked');
  }
  function startScroll(key) {
    delete locks[key];
    if (lockCount() === 0) {
      root.classList.remove('lu-locked');
      if (lenis && !document.body.classList.contains('overflow-hidden')) lenis.start();
    }
  }

  function initLenis() {
    if (cfg.smoothScroll === false || reduceMotion || designMode) return;
    var i = 0;
    function load() {
      if (i >= LENIS_URLS.length) return;
      var url = LENIS_URLS[i++];
      import(url).then(function (m) {
        var Lenis = m.default || m.Lenis;
        lenis = new Lenis({
          smoothWheel: true,
          prevent: function (node) {
            return !!(node.closest && node.closest(
              '[data-lenis-prevent], .cart-drawer, cart-drawer, .cart-notification, .drawer, .modal__content, .predictive-search, .lu-modal, .lu-navmenu, .facets-container-drawer, .menu-drawer'
            ));
          }
        });
        window.lenis = lenis;
        if (lockCount() > 0) lenis.stop();
        var raf = function (t) { lenis.raf(t); requestAnimationFrame(raf); };
        requestAnimationFrame(raf);
      }).catch(load);
    }
    try { load(); } catch (e) { /* dynamic import unsupported: native scroll */ }

    // Dawn locks the page with body.overflow-hidden for its own drawers / modals.
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (!lenis) return;
        if (document.body.classList.contains('overflow-hidden')) lenis.stop();
        else if (lockCount() === 0) lenis.start();
      }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
  }

  function scrollToEl(el) {
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: 0 });
    else {
      var top = el.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }

  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    var id = a.getAttribute('href').slice(1);
    if (!id || a.hasAttribute('data-lu-request')) return;
    var target = document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    scrollToEl(target);
  });

  /* ---------------------------------------------------------- ready gate */
  var isReady = false;
  var pendingGate = [];
  function setReady() {
    if (isReady) return;
    isReady = true;
    root.classList.add('lu-ready');
    pendingGate.splice(0).forEach(revealEl);
  }

  /* --------------------------------------------------------- text splits */
  function splitWords(el) {
    var idx = 0;
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (ch) {
        if (ch.nodeType === 3) {
          var frag = document.createDocumentFragment();
          ch.textContent.split(/(\s+)/).forEach(function (p) {
            if (!p) return;
            if (/^\s+$/.test(p)) frag.appendChild(document.createTextNode(p));
            else {
              var s = document.createElement('span');
              s.className = 'lu-word';
              s.style.setProperty('--i', idx++);
              s.textContent = p;
              frag.appendChild(s);
            }
          });
          node.replaceChild(frag, ch);
        } else if (ch.nodeType === 1) walk(ch);
      });
    })(el);
    el.classList.add('lu-split');
  }

  function splitLines(el) {
    var text = el.getAttribute('data-lu-text');
    if (text === null) { text = el.textContent.replace(/\s+/g, ' ').trim(); el.setAttribute('data-lu-text', text); }
    if (!text) return;
    el.setAttribute('aria-label', text);
    el.innerHTML = '';
    var words = text.split(' ');
    var spans = words.map(function (w, i) {
      var s = document.createElement('span');
      s.textContent = w + (i < words.length - 1 ? ' ' : '');
      el.appendChild(s);
      return s;
    });
    var lines = [], lastTop = null;
    spans.forEach(function (s, i) {
      var top = s.offsetTop;
      if (lastTop === null || Math.abs(top - lastTop) > 2) { lines.push([]); lastTop = top; }
      lines[lines.length - 1].push(words[i]);
    });
    el.innerHTML = '';
    lines.forEach(function (ws, i) {
      var line = document.createElement('span');
      line.className = 'lu-line';
      line.setAttribute('aria-hidden', 'true');
      var inner = document.createElement('span');
      inner.className = 'lu-line__in';
      inner.style.setProperty('--i', i);
      inner.textContent = ws.join(' ');
      line.appendChild(inner);
      el.appendChild(line);
    });
    el.classList.add('lu-split');
  }

  function initTextSplits() {
    $$('.lu-lines').forEach(function (el) {
      if (el.getAttribute('data-lines') === 'explicit') el.classList.add('lu-split');
    });
    var run = function () {
      $$('.lu-lines:not([data-lines="explicit"])').forEach(splitLines);
      $$('.lu-words:not(.lu-split)').forEach(splitWords);
      // anything that was already visible while waiting for the split
      observeAll();
    };
    if (document.fonts && document.fonts.ready) {
      // never hold the headline back for more than 400ms waiting on web fonts (keeps LCP fast)
      var ran = false, go = function () { if (ran) return; ran = true; run(); };
      document.fonts.ready.then(go); setTimeout(go, 400);
    } else run();
    var lastW = window.innerWidth, t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        if (window.innerWidth === lastW) return;
        lastW = window.innerWidth;
        $$('.lu-lines:not([data-lines="explicit"])').forEach(splitLines);
      }, 200);
    });
  }

  /* ------------------------------------------------------------- reveals */
  var io = null;
  function revealEl(el) {
    if (el.getAttribute('data-gate') === 'ready' && !isReady) {
      if (pendingGate.indexOf(el) === -1) pendingGate.push(el);
      return;
    }
    el.classList.add('is-in');
  }
  function observeAll() {
    var els = $$('.lu-reveal, .lu-lines, .lu-words');
    if (!('IntersectionObserver' in window) || designMode) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          var el = en.target;
          if (el.classList.contains('lu-lines') || el.classList.contains('lu-words')) {
            if (!el.classList.contains('lu-split')) return; // wait for the split, observeAll() re-observes
          }
          io.unobserve(el);
          revealEl(el);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -4% 0px' });
    }
    els.forEach(function (el) {
      if (el.classList.contains('is-in') || el.__luObserved === (el.classList.contains('lu-split') ? 2 : 1)) return;
      el.__luObserved = el.classList.contains('lu-split') ? 2 : 1;
      io.unobserve(el); // re-observing fires a fresh intersection callback
      io.observe(el);
    });
  }

  /* -------------------------------------------------------------- loader */
  function initLoader() {
    var el = $('[data-lu-loader]');
    if (!el) { setReady(); return; }
    var once = el.getAttribute('data-once') === 'true';
    try { if (once && sessionStorage.getItem('lu-loader-seen')) { el.remove(); setReady(); return; } } catch (e) {}
    if (designMode || reduceMotion) { el.remove(); setReady(); return; }

    stopScroll('loader');
    var fill = $('[data-lu-loader-fill]', el);
    var count = $('[data-lu-loader-count]', el);
    var FILL_MS = 1300, start = null, finished = false;
    var ease = function (t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; };

    function done() {
      if (finished) return;
      finished = true;
      el.remove();
      try { sessionStorage.setItem('lu-loader-seen', '1'); } catch (e) {}
      startScroll('loader');
      window.scrollTo(0, 0);
      setReady();
    }
    function exit() {
      el.classList.add('is-leaving');
      el.addEventListener('transitionend', function (ev) { if (ev.target === el && ev.propertyName === 'transform') done(); });
      setTimeout(done, 1100);
    }
    function step(ts) {
      if (start === null) start = ts;
      var t = clamp((ts - start) / FILL_MS, 0, 1);
      var p = Math.round(ease(t) * 100);
      fill.style.width = p + '%';
      count.textContent = ('00' + p).slice(-3);
      if (t < 1) requestAnimationFrame(step); else exit();
    }
    requestAnimationFrame(step);
    setTimeout(function () { if (!finished) { el.classList.add('is-leaving'); setTimeout(done, 900); } }, 8000);
  }

  /* --------------------------------------------------------------- clock */
  function initClock() {
    var times = $$('[data-lu-clock-time]'), dates = $$('[data-lu-clock-date]');
    if (!times.length && !dates.length) return;
    var months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    function tick() {
      var d = new Date(), h = d.getHours();
      var t = (h % 12 || 12) + ':' + pad(d.getMinutes()) + (h >= 12 ? 'pm' : 'am');
      var s = d.getDate() + ' ' + months[d.getMonth()] + ', ' + d.getFullYear();
      times.forEach(function (n) { n.textContent = t; });
      dates.forEach(function (n) { n.textContent = s; });
    }
    tick();
    setInterval(tick, 1000);
  }

  /* ----------------------------------------------------------- count-up */
  function initCountUp() {
    var els = $$('[data-lu-count]');
    if (!els.length) return;
    els.forEach(function (el) { el.__luTarget = parseFloat(el.getAttribute('data-lu-count')) || 0; });
    var last = 0, queued = false;
    function update() {
      queued = false;
      last = performance.now();
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var r = el.getBoundingClientRect();
        // 0 when the top reaches the viewport bottom, 1 when the centre reaches the viewport centre
        var p = clamp((vh - r.top) / (vh / 2 + r.height / 2), 0, 1);
        el.textContent = Math.round(p * el.__luTarget);
      });
    }
    function onScroll() {
      if (queued) return;
      queued = true;
      var wait = Math.max(0, 30 - (performance.now() - last));
      setTimeout(function () { requestAnimationFrame(update); }, wait);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    if (designMode) els.forEach(function (el) { el.textContent = el.__luTarget; }); else update();
  }

  /* -------------------------------------------------------- hero carousel */
  function initHeroCard() {
    $$('[data-lu-herocard]').forEach(function (card) {
      var items = $$('.lu-herocard__item', card), dots = $$('.lu-herocard__dots i', card);
      var n = items.length, cur = 0;
      if (n < 2) return;
      function go(step) {
        var next = (cur + step + n) % n;
        var out = items[cur], inn = items[next];
        // incoming item starts on the opposite side of where the outgoing one leaves to
        inn.style.transition = 'none';
        inn.classList.remove('is-up');
        if (step < 0) inn.classList.add('is-up');
        void inn.offsetWidth;
        inn.style.transition = '';
        out.classList.remove('is-active');
        if (step > 0) out.classList.add('is-up'); else out.classList.remove('is-up');
        inn.classList.remove('is-up');
        inn.classList.add('is-active');
        items.forEach(function (it, i) { if (i !== next && i !== cur) it.classList.remove('is-up', 'is-active'); });
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i === next); });
        cur = next;
      }
      card.addEventListener('click', function (e) {
        if (e.target.closest('a')) return; // product / collection links navigate normally
        var prev = e.target.closest('[data-lu-herocard-prev]');
        if (prev) { e.stopPropagation(); go(-1); return; }
        if (e.target.closest('[data-lu-herocard-next]')) { go(1); }
      });
    });
  }

  /* ---------------------------------------------------- liquid cursor reveal */
  function initLiquid() {
    $$('[data-lu-liquid]').forEach(function (container) {
      var canvas = $('canvas', container);
      var src = container.getAttribute('data-reveal-src');
      if (!canvas || !src) return;
      if (reduceMotion) { canvas.remove(); return; }

      var brushCss = parseFloat(container.getAttribute('data-brush')) || 143;
      var decay = parseFloat(container.getAttribute('data-decay')) || 0.016;
      var mode = container.getAttribute('data-mode') || 'liquid';
      var lens = { x: -9999, y: -9999, tx: -9999, ty: -9999, a: 0, on: false };
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var ctx = canvas.getContext('2d');
      var cover = document.createElement('canvas'), cctx = cover.getContext('2d');
      var brush = document.createElement('canvas'), bctx = brush.getContext('2d');
      var afterImg = new Image();
      var loaded = false, w = 1, h = 1, radius = brushCss * dpr, points = [], last = null, idle = 0, running = false;

      function measure() {
        var rect = container.getBoundingClientRect();
        w = Math.max(1, Math.round(rect.width * dpr));
        h = Math.max(1, Math.round(rect.height * dpr));
        canvas.width = w; canvas.height = h;
        cover.width = w; cover.height = h;
        if (loaded) {
          var iw = afterImg.naturalWidth, ih = afterImg.naturalHeight;
          var s = Math.max(w / iw, h / ih), dw = iw * s, dh = ih * s;
          cctx.clearRect(0, 0, w, h);
          cctx.drawImage(afterImg, (w - dw) / 2, (h - dh) / 2, dw, dh);
        }
        radius = brushCss * dpr;
        var d = Math.ceil(radius * 2);
        brush.width = d; brush.height = d;
      }

      function stamp(x, y) {
        if (mode === 'bubbles') {
          for (var k = 0; k < 3; k++) {
            var ang = Math.random() * 6.283, dist = Math.random() * radius * 0.7;
            var px = x + Math.cos(ang) * dist, py = y + Math.sin(ang) * dist;
            var rb = radius * (0.14 + Math.random() * 0.3);
            ctx.save();
            ctx.globalCompositeOperation = 'source-over';
            ctx.beginPath(); ctx.arc(px, py, rb, 0, 6.283); ctx.clip();
            ctx.drawImage(cover, 0, 0);
            ctx.restore();
            ctx.beginPath(); ctx.arc(px, py, rb, 0, 6.283);
            ctx.strokeStyle = 'rgba(255,255,255,0.55)'; ctx.lineWidth = Math.max(1, dpr); ctx.stroke();
          }
          return;
        }
        if (mode === 'diamond' || mode === 'star' || mode === 'heart' || mode === 'stripes' || mode === 'ring') {
          var R = radius * 0.9;
          ctx.save();
          ctx.globalCompositeOperation = 'source-over';
          ctx.beginPath();
          if (mode === 'diamond') {
            ctx.moveTo(x, y - R); ctx.lineTo(x + R * 0.8, y); ctx.lineTo(x, y + R); ctx.lineTo(x - R * 0.8, y); ctx.closePath();
          } else if (mode === 'star') {
            for (var si = 0; si < 10; si++) {
              var rr = si % 2 === 0 ? R : R * 0.45, an = -Math.PI / 2 + si * Math.PI / 5;
              if (si === 0) ctx.moveTo(x + Math.cos(an) * rr, y + Math.sin(an) * rr); else ctx.lineTo(x + Math.cos(an) * rr, y + Math.sin(an) * rr);
            }
            ctx.closePath();
          } else if (mode === 'heart') {
            var hs = R * 0.95;
            ctx.moveTo(x, y + hs * 0.9);
            ctx.bezierCurveTo(x - hs * 1.2, y + hs * 0.1, x - hs * 0.8, y - hs * 0.9, x, y - hs * 0.35);
            ctx.bezierCurveTo(x + hs * 0.8, y - hs * 0.9, x + hs * 1.2, y + hs * 0.1, x, y + hs * 0.9);
            ctx.closePath();
          } else if (mode === 'stripes') {
            var gap = R * 0.32, sw = R * 0.14;
            for (var k = -3; k <= 3; k++) ctx.rect(x + k * gap - sw / 2, y - R, sw, R * 2);
          } else {
            ctx.arc(x, y, R, 0, 6.283);
            ctx.moveTo(x + R * 0.55, y);
            ctx.arc(x, y, R * 0.55, 0, 6.283, true);
          }
          ctx.clip();
          ctx.drawImage(cover, 0, 0);
          ctx.restore();
          return;
        }
        if (mode === 'card') {
          var half = radius * 0.8, r = half * 0.28;
          ctx.save();
          ctx.globalCompositeOperation = 'source-over';
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(x - half, y - half, half * 2, half * 2, r); else ctx.rect(x - half, y - half, half * 2, half * 2);
          ctx.clip();
          ctx.drawImage(cover, 0, 0);
          ctx.restore();
          return;
        }
        var d = brush.width, c = d / 2;
        bctx.clearRect(0, 0, d, d);
        bctx.globalCompositeOperation = 'source-over';
        var g = bctx.createRadialGradient(c, c, 0, c, c, c);
        g.addColorStop(0, 'rgba(255,255,255,1)');
        g.addColorStop(0.55, 'rgba(255,255,255,0.82)');
        g.addColorStop(1, 'rgba(255,255,255,0)');
        bctx.fillStyle = g;
        bctx.fillRect(0, 0, d, d);
        bctx.globalCompositeOperation = 'source-in';
        bctx.drawImage(cover, x - c, y - c, d, d, 0, 0, d, d);
        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(brush, x - c, y - c);
      }

      function tickLens() {
        lens.x += (lens.tx - lens.x) * 0.2; lens.y += (lens.ty - lens.y) * 0.2;
        lens.a += ((lens.on ? 1 : 0) - lens.a) * 0.12;
        ctx.globalCompositeOperation = 'source-over';
        ctx.clearRect(0, 0, w, h);
        if (lens.a > 0.01) {
          var d = brush.width, c = d / 2;
          bctx.clearRect(0, 0, d, d);
          bctx.globalCompositeOperation = 'source-over';
          var g = bctx.createRadialGradient(c, c, 0, c, c, c);
          g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.86, 'rgba(255,255,255,1)'); g.addColorStop(1, 'rgba(255,255,255,0)');
          bctx.fillStyle = g; bctx.fillRect(0, 0, d, d);
          bctx.globalCompositeOperation = 'source-in';
          bctx.drawImage(cover, lens.x - c, lens.y - c, d, d, 0, 0, d, d);
          ctx.globalAlpha = lens.a;
          ctx.drawImage(brush, lens.x - c, lens.y - c);
          ctx.beginPath(); ctx.arc(lens.x, lens.y, c * 0.86, 0, 6.283);
          ctx.strokeStyle = 'rgba(255,255,255,0.7)'; ctx.lineWidth = 2 * dpr; ctx.stroke();
          ctx.globalAlpha = 1;
          requestAnimationFrame(tickLens);
        } else if (lens.on) requestAnimationFrame(tickLens);
        else { running = false; }
      }
      function tick() {
        if (mode === 'lens') return tickLens();
        var drawing = points.length > 0;
        if (drawing) idle = 0;
        else {
          idle++;
          if (mode === 'scratch' && idle > 5) { running = false; return; }
          if (idle > 120) { ctx.clearRect(0, 0, w, h); running = false; return; }
        }
        if (mode !== 'scratch') {
          var fade = drawing ? decay : Math.min(decay + idle * 0.004, 0.5);
          ctx.globalCompositeOperation = 'destination-out';
          ctx.fillStyle = 'rgba(0,0,0,' + fade + ')';
          ctx.fillRect(0, 0, w, h);
        }
        if (drawing) {
          for (var i = 0; i < points.length; i++) stamp(points[i][0], points[i][1]);
          points.length = 0;
        }
        requestAnimationFrame(tick);
      }
      function wake() { if (!running && loaded) { running = true; idle = 0; requestAnimationFrame(tick); } }

      function onMove(e) {
        if (!loaded) return;
        var rect = container.getBoundingClientRect();
        var x = (e.clientX - rect.left) * dpr, y = (e.clientY - rect.top) * dpr;
        if (x < -radius || y < -radius || x > w + radius || y > h + radius) { last = null; lens.on = false; return; }
        if (mode === 'lens') {
          if (!lens.on) { lens.x = x; lens.y = y; }
          lens.tx = x; lens.ty = y; lens.on = true;
          clearTimeout(lens.t); lens.t = setTimeout(function () { lens.on = false; wake(); }, 1400);
          wake(); return;
        }
        if (last) {
          var dx = x - last[0], dy = y - last[1], dist = Math.sqrt(dx * dx + dy * dy);
          var step = Math.max(radius * ((mode === 'heart' || mode === 'star' || mode === 'diamond') ? 0.55 : 0.3), 1), n = Math.min(Math.ceil(dist / step), 60);
          for (var i = 1; i <= n; i++) points.push([last[0] + (dx * i) / n, last[1] + (dy * i) / n]);
        } else points.push([x, y]);
        last = [x, y];
        wake();
      }

      afterImg.onload = function () { loaded = true; measure(); };
      afterImg.src = src;
      measure();
      window.addEventListener('pointermove', onMove, { passive: true });
      if (window.ResizeObserver) new ResizeObserver(measure).observe(container);
      else window.addEventListener('resize', measure);
    });
  }

  /* ------------------------------------------------------------ nav menu */
  function initNavMenu() {
    var menu = $('[data-lu-navmenu]');
    if (!menu) return;
    var opener = null;
    function open(btn) {
      opener = btn || null;
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      stopScroll('nav');
      var c = $('[data-lu-menu-close]', menu);
      if (c) setTimeout(function () { c.focus({ preventScroll: true }); }, 50);
    }
    function close() {
      if (!menu.classList.contains('is-open')) return;
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      startScroll('nav');
      if (opener) opener.focus({ preventScroll: true });
    }
    menu.__luClose = close;
    document.addEventListener('click', function (e) {
      var o = e.target.closest('[data-lu-menu-open]');
      if (o) { open(o); return; }
      if (e.target.closest('[data-lu-menu-close]')) { close(); return; }
      if (e.target.closest('[data-lu-menu-link]')) close();
    });
  }

  /* --------------------------------------------------------------- modal */
  function initModal() {
    var modal = $('[data-lu-modal]');
    if (!modal) return;
    var backdrop = $('[data-lu-modal-backdrop]', modal), panel = $('[data-lu-modal-panel]', modal);
    var formState = $('[data-lu-modal-form-state]', modal), success = $('[data-lu-modal-success]', modal);
    var form = $('form', modal), submit = $('[data-lu-modal-submit]', modal);
    var submitLabel = $('[data-lu-modal-submit-label]', modal), err = $('[data-lu-modal-error]', modal);
    var opener = null, isOpen = false;

    function open(btn) {
      if (isOpen) return;
      isOpen = true; opener = btn || null;
      modal.hidden = false;
      modal.classList.remove('is-closing');
      void modal.offsetWidth;
      modal.classList.add('is-open');
      stopScroll('modal');
      var first = $('input', modal);
      if (first) setTimeout(function () { first.focus({ preventScroll: true }); }, 120);
    }
    function close() {
      if (!isOpen) return;
      isOpen = false;
      modal.classList.add('is-closing');
      modal.classList.remove('is-open');
      startScroll('modal');
      setTimeout(function () {
        modal.hidden = true;
        modal.classList.remove('is-closing');
        if (form) form.reset();
        formState.hidden = false; success.hidden = true; err.hidden = true;
        submitLabel.textContent = submitLabel.getAttribute('data-label');
        submit.disabled = false;
      }, 350);
      if (opener && opener.focus) opener.focus({ preventScroll: true });
    }

    document.addEventListener('click', function (e) {
      var r = e.target.closest('[data-lu-request]');
      if (r) {
        e.preventDefault();
        var nav = $('[data-lu-navmenu]');
        if (nav && nav.__luClose) nav.__luClose();
        open(r);
        return;
      }
      if (e.target.closest('[data-lu-modal-close]')) close();
    });
    backdrop.addEventListener('click', function (e) { if (e.target === backdrop) close(); });
    panel.addEventListener('click', function (e) { e.stopPropagation(); });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (isOpen) { close(); return; }
      var nav = $('[data-lu-navmenu]');
      if (nav && nav.__luClose) nav.__luClose();
    });

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (submit.disabled) return;
        submit.disabled = true;
        err.hidden = true;
        submitLabel.textContent = 'Sending…';
        fetch(form.getAttribute('action') || '/contact', {
          method: 'POST',
          body: new FormData(form),
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        }).then(function (res) {
          if (res.url && res.url.indexOf('/challenge') > -1) { form.submit(); return; } // captcha needed: normal submit
          if (res.ok && res.url && res.url.indexOf('contact_posted=true') > -1) {
            formState.hidden = true; success.hidden = false;
            var b = $('button', success); if (b) b.focus({ preventScroll: true });
          } else throw new Error('failed');
        }).catch(function () {
          err.hidden = false;
          submit.disabled = false;
          submitLabel.textContent = submitLabel.getAttribute('data-label');
        });
      });
    }
  }

  /* ---------------------------------------------------------------- boot */
  function boot() {
    try { initLenis(); } catch (e) {}
    try { initTextSplits(); } catch (e) {}
    observeAll();
    try { initClock(); initCountUp(); initHeroCard(); initLiquid(); initNavMenu(); initModal(); } catch (e) { if (window.console) console.error('[lumora]', e); }
    initLoader();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Re-run for sections re-rendered inside the theme editor
  document.addEventListener('shopify:section:load', function () {
    initTextSplits(); observeAll(); initHeroCard(); initLiquid();
  });
})();
